import React, {
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactElement,
} from 'react';
import { IconBaseProps } from 'react-icons';
import { FiAlertCircle } from 'react-icons/fi';

import { useField } from '@unform/core';

import { Container, Error } from './styles';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  // overwrite the requirement of having a name in input, which was not required
  name: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  containerStyle?: object;
  // Icon was a custom prop defined in index.tsx SignIn
  icon?: React.ComponentType<IconBaseProps>;
  invisible?: boolean;
}

function Input({
  name,
  containerStyle = {},
  icon: Icon,
  invisible,
  ...rest
}: InputProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const { fieldName, defaultValue, error, registerField } = useField(name);

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);

    setIsFilled(!!inputRef.current?.value);
  }, []);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [fieldName, registerField]);

  return (
    <Container
      style={containerStyle}
      iserrored={!!error}
      isfilled={isFilled}
      isfocused={isFocused}
      invisible={invisible}
      data-testid="input-container"
      id="inputContainer"
    >
      {/*  If icon exists then show icon */}
      {Icon && <Icon size={18} />}
      <input
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        defaultValue={defaultValue}
        ref={inputRef}
        {...rest}
      />
      {/* If the error exists than show the icon for the error */}
      {error && (
        <Error title={error}>
          <FiAlertCircle color="#c53030" size={18} />
        </Error>
      )}
    </Container>
  );
}

// Input.defaultProps = {
//   containerStyle: {},
//   icon: React.Component,
// };

export default Input;
